// ViewControls - Display settings panel
import React, { useState } from 'react';
import { ThemeType, ShapeType, ViewSettings } from '../../types/visualization';
import { THEMES, isMobile } from '../../constants/visualizationThemes';

interface ViewControlsProps {
  settings: ViewSettings;
  onChange: (key: keyof ViewSettings, value: boolean | number) => void;
  onResetCamera: () => void;
  theme: ThemeType;
  onThemeChange: (theme: ThemeType) => void;
  metallicMode: boolean;
  onMetallicChange: (enabled: boolean) => void;
  shapeType: ShapeType;
  onShapeChange: (shape: ShapeType) => void;
}

export default function ViewControls({
  settings,
  onChange,
  onResetCamera,
  theme,
  onThemeChange,
  metallicMode,
  onMetallicChange,
  shapeType,
  onShapeChange,
}: ViewControlsProps) {
  const [collapsed, setCollapsed] = useState(() => isMobile());
  const [sectionsOpen, setSectionsOpen] = useState({
    display: true,
    shape: false,
  });
  const t = THEMES[theme];

  const toggleSection = (section: 'display' | 'shape') => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const labelStyle: React.CSSProperties = {
    marginBottom: '6px',
    color: t.textMuted,
    fontSize: '9px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontWeight: 500,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '10px',
  };

  const SectionHeader = ({ title, section, isOpen }: { title: string; section: 'display' | 'shape'; isOpen: boolean }) => (
    <div
      onClick={() => toggleSection(section)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0',
        cursor: 'pointer',
        userSelect: 'none',
        borderBottom: `1px solid ${t.panelBorder}`,
        marginBottom: isOpen ? '10px' : '0',
      }}
    >
      <span style={{ fontSize: '9px', fontWeight: 600, color: t.textMuted, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        {title}
      </span>
      <span style={{ fontSize: '8px', color: t.textDimmed, transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s ease' }}>
        ▼
      </span>
    </div>
  );

  const ToggleButton = ({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) => (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '5px',
        background: active ? t.accent : t.buttonBg,
        border: 'none',
        borderRadius: '3px',
        color: active ? (theme === 'dark' ? '#000' : '#fff') : t.textMuted,
        fontSize: '9px',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        transition: 'all 0.1s ease',
      }}
    >
      {children}
    </button>
  );

  const Slider = ({ value, min, max, step, onSliderChange }: {
    value: number;
    min: number;
    max: number;
    step: number;
    onSliderChange: (value: number) => void;
  }) => (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onSliderChange(parseFloat(e.target.value))}
      style={{
        width: '100%',
        height: '3px',
        background: t.buttonBorder,
        borderRadius: '2px',
        appearance: 'none',
        cursor: 'pointer',
      }}
    />
  );

  return (
    <div style={{
      position: 'absolute',
      bottom: '20px',
      left: '20px',
      background: t.panelBg,
      border: `1px solid ${t.panelBorder}`,
      borderRadius: '8px',
      color: t.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backdropFilter: 'blur(20px)',
      width: collapsed ? 'auto' : '165px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      transition: 'width 0.2s ease',
      zIndex: 100,
    }}>
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${t.panelBorder}`,
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '9px', fontWeight: 600, color: t.textMuted, letterSpacing: '0.5px' }}>
          VIEW
        </span>
        <span style={{ fontSize: '10px', color: t.textMuted, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
          ▼
        </span>
      </div>

      {!collapsed && (
        <div style={{ padding: '10px', paddingTop: '8px' }}>
          <SectionHeader title="Display" section="display" isOpen={sectionsOpen.display} />
          {sectionsOpen.display && (
            <>
              <div style={sectionStyle}>
                <div style={labelStyle}>Theme</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  <ToggleButton active={theme === 'dark'} onClick={() => onThemeChange('dark')}>
                    Dark
                  </ToggleButton>
                  <ToggleButton active={theme === 'light'} onClick={() => onThemeChange('light')}>
                    Light
                  </ToggleButton>
                </div>
              </div>

              <div style={sectionStyle}>
                <div style={labelStyle}>Auto Rotate</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  <ToggleButton active={settings.autoRotate} onClick={() => onChange('autoRotate', true)}>
                    On
                  </ToggleButton>
                  <ToggleButton active={!settings.autoRotate} onClick={() => onChange('autoRotate', false)}>
                    Off
                  </ToggleButton>
                </div>
              </div>

              <div style={sectionStyle}>
                <div style={labelStyle}>Stars</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  <ToggleButton active={settings.showStars} onClick={() => onChange('showStars', true)}>
                    Show
                  </ToggleButton>
                  <ToggleButton active={!settings.showStars} onClick={() => onChange('showStars', false)}>
                    Hide
                  </ToggleButton>
                </div>
              </div>

              <div style={sectionStyle}>
                <div style={{ ...labelStyle, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Anim Speed</span>
                  <span style={{ color: t.textDimmed }}>{settings.animSpeed.toFixed(1)}x</span>
                </div>
                <Slider
                  value={settings.animSpeed}
                  min={0.1}
                  max={3}
                  step={0.1}
                  onSliderChange={(v) => onChange('animSpeed', v)}
                />
              </div>
            </>
          )}

          <SectionHeader title="Shape" section="shape" isOpen={sectionsOpen.shape} />
          {sectionsOpen.shape && (
            <>
              <div style={sectionStyle}>
                <div style={labelStyle}>Surface</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  <ToggleButton active={!metallicMode} onClick={() => onMetallicChange(false)}>
                    Standard
                  </ToggleButton>
                  <ToggleButton active={metallicMode} onClick={() => onMetallicChange(true)}>
                    Metallic
                  </ToggleButton>
                </div>
              </div>

              <div style={sectionStyle}>
                <div style={labelStyle}>Type</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  <ToggleButton active={shapeType === 'cube'} onClick={() => onShapeChange('cube')}>
                    Cube
                  </ToggleButton>
                  <ToggleButton active={shapeType === 'pyramid'} onClick={() => onShapeChange('pyramid')}>
                    Pyr
                  </ToggleButton>
                  <ToggleButton active={shapeType === 'sphere'} onClick={() => onShapeChange('sphere')}>
                    Sph
                  </ToggleButton>
                </div>
              </div>
            </>
          )}

          <button
            onClick={onResetCamera}
            style={{
              width: '100%',
              padding: '6px',
              marginTop: '6px',
              background: t.buttonBg,
              border: `1px solid ${t.buttonBorder}`,
              borderRadius: '3px',
              color: t.textMuted,
              fontSize: '9px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.1s ease',
            }}
          >
            ↺ Reset Camera
          </button>
        </div>
      )}
    </div>
  );
}
